/**
 * Color science for event pages & the events calendar.
 *
 * Reuses the primitives in `color.util.ts` but owns the event/calendar-specific
 * palette logic. The whole feature reduces to deciding a small set of style
 * colors from up to two inputs — a background color and a content color — and
 * letting the existing marker-class assignment render them.
 *
 * See docs/events-custom-bg-color/plan.md (§3) for the full model.
 */
import type {Instance} from 'tinycolor2';
import tinycolor from 'tinycolor2';
import {
  adjustColorTone,
  darkenColor,
  lightenColor,
  getColorPaletteForContainer,
  getDarkenedColorToneHex,
  getReadableTextColorForBackground,
  PMW_COLORS_NEUTRAL,
} from '@Utils/color.util';

/** WCAG AA contrast ratio for normal text — the legibility gate for content vs. background. */
export const MIN_TEXT_CONTRAST = 4.5;
/** WCAG contrast ratio for UI components / large text — used so a derived accent reads as a distinct surface. */
export const MIN_ACCENT_CONTRAST = 3;

const SHIFT_START = 50;
const SHIFT_STEP = 5;
const SHIFT_MAX = 90;
const LIGHTNESS_MIDPOINT = 50;

const ACCENT_LIGHTEN = 0.35;
const ACCENT_DARKEN = 0.3;
const ACCENT_SATURATION_BOOST = 0.15;
const ACCENT_NUDGE_STEP = 0.03;
const ACCENT_NUDGE_MAX_ITERATIONS = 20;
const ACCENT_HOVER_SHIFT_PERCENT = 6;
/**
 * Hover also deepens the colour, not just its lightness. Lightness alone had to move far enough to
 * read as a change, and on a light page — where the accent already sits close to the background —
 * that cost the button its separation (`#f7f9fc` measured 4.80 -> 2.36). Splitting the change across
 * both axes lets the lightness step be small while the hover still reads as a distinct state.
 */
const ACCENT_HOVER_SATURATION_BOOST = 0.14;
/**
 * The lightness step for a greyscale accent, which gets no saturation boost to help it (see
 * `getAccentHoverColor`). With only one axis left, the step has to carry the whole state change:
 * measured on `#000000`, 6% moves the hover 1.26:1 off the rest fill — below the 1.38 the shipped
 * two-axis step reached — while 10% reaches 1.50 and lifts the label from 6.19 to 9.29.
 */
const ACHROMATIC_HOVER_SHIFT_PERCENT = 10;

/**
 * Saturation at or below which a color carries no usable hue. tinycolor reports `h = 0` (red) for
 * every achromatic color, so anything that boosts saturation would invent a red that was never
 * there — which is exactly how a black page came out with a brown button.
 */
const ACHROMATIC_SATURATION_MAX = 0.05;

const SURFACE_SHIFT_1 = 0.08;
const SURFACE_SHIFT_2 = 0.14;

/**
 * Relative-luminance cutoff above which a CTA fill is "light" and needs a dark
 * label; at or below it we keep white (the design-system button look). Tuned so
 * saturated brand colors (blue/green/red/purple) keep white and only pale fills
 * (yellow, near-white, light grey) flip to dark.
 */
const CTA_LIGHT_FILL_LUMINANCE = 0.5;

/**
 * Same-hue dark ink for the pale-fill CTA label: keep the fill's hue at a
 * softened saturation and a low lightness, so a light button (yellow, pale
 * salmon, near-white) gets a label that reads as part of the button rather than
 * a generic black. Achromatic fills (white/grey) fall back to a dark neutral.
 */
const CTA_INK_SATURATION_FACTOR = 0.6;
const CTA_INK_LIGHTNESS = 0.16;

const WHITE_HEX = 'ffffff';
const NEUTRAL_0_HEX = tinycolor(PMW_COLORS_NEUTRAL.NEUTRAL_0).toHexString();

/**
 * The set of computed colors that drive an events surface — the event page and the events
 * calendar both render from exactly this palette.
 *
 * What each color *means*, since the legacy field names describe shades rather than roles:
 *
 * | Field             | Role                    | Derived from | Applied to |
 * |-------------------|-------------------------|--------------|------------|
 * | `background`      | page background         | user's pick  | the page container, and every card (D8) |
 * | `dark`            | **page text**           | `background` | title, headings, body, dates, contact, footer |
 * | `primary`         | **accent** (fill)       | `background` | button/CTA fill (Register, Follow, list CTAs) + the seed for calendar tiles |
 * | `darker`          | accent hover            | `primary`    | button `:hover`/`:focus`/`:active` fill |
 * | `light`/`lighter` | surfaces                | `background` | secondary surfaces; `light` is the alternative card fill behind the V1 knob |
 * | `buttonTextColor` | **button label**        | `primary`    | the label sitting on the accent fill |
 *
 * Two independent contrast guarantees hold: the page text clears **4.5:1 against the background**,
 * and the accent clears **3:1 against the background** so a button is visible as an object. The
 * button label is then measured against the *accent*, not the background — a distinct third
 * relationship, and the one that made a white label fail at 3.04:1 on a mid-tone accent.
 */
export interface EventPaletteResult {
  /** Accent — the button/CTA fill, and the calendar tile seed (`styleColorID`). */
  primary: string;
  /** Page text, derived from the background to clear 4.5:1 (`styleDarkColorID`). */
  dark: string;
  /** Accent hover/active shade. */
  darker: string;
  /** Surface background (`styleLightBgColorID`). */
  light: string;
  /** Secondary surface background (`styleLighterBgColorID`). */
  lighter: string;
  /** Label color for text sitting on `primary` — replaces the previously hard-coded white. */
  buttonTextColor: string;
  /** Page background; `null` means "no custom background" (Mode A, page stays neutral). */
  background: string | null;
}

/** Page-shell colors for the events calendar (the month grid keeps its own palette). */
export interface CalendarPageColors {
  /** Page-shell background; `null` means no custom background (render as today). */
  background: string | null;
  /** Legible page-shell text for the chosen background. */
  text: string;
  /**
   * Full derived theme for the shell — accent, surfaces and button label. Used to theme the
   * server-rendered list/grid cards, every design-system button, and to seed the React
   * calendar-view tiles (decisions D9/D10). `null` in Mode A, where the calendar renders
   * exactly as it did before this feature.
   */
  theme: EventPaletteResult | null;
}

/** True when a color is white — treated as "no custom background" (Mode A). */
export const isWhite = (colorHex: string): boolean => {
  return tinycolor(colorHex).toHex() === WHITE_HEX;
};

/** True when a background's lightness is on the dark half, so foreground colors should move lighter. */
const isDarkLightness = (lightness01: number): boolean => {
  return lightness01 * 100 < LIGHTNESS_MIDPOINT;
};

/** WCAG contrast ratio between two colors (1–21). */
export const getContrastRatio = (colorA: string, colorB: string): number => {
  return tinycolor.readability(colorA, colorB);
};

/** True when the two colors meet the WCAG AA normal-text contrast gate. */
export const isLegible = (backgroundHex: string, contentHex: string, minContrast: number = MIN_TEXT_CONTRAST): boolean => {
  return getContrastRatio(backgroundHex, contentHex) >= minContrast;
};

const shiftSurface = (background: Instance, lightness01: number, goingLighter: boolean, shift: number): string => {
  const lightness = goingLighter ? Math.min(1, lightness01 + shift) : Math.max(0, lightness01 - shift);
  return adjustColorTone(background, lightness).toHexString();
};

/**
 * Derive a legible text color for a background by flipping its lightness while
 * keeping hue and saturation (plan §3.2). Starts at a 50-point lightness shift
 * and increases by 5 until the contrast gate is met, capped at 90.
 *
 * Gated against the **deepest derived surface as well as the background**, because the text does not
 * only sit on the page — the date chip and any secondary surface put it on a same-hue shade that has
 * already moved partway toward it. Stopping at the first shift that cleared 4.5:1 against the *page*
 * left no margin for that: text measuring 7.88:1 on `#00303f` measured 3.69:1 on the chip, and on
 * `#f7f9fc` the chip's two bands measured 3.91 and 3.31. Asking for the margin up front costs
 * nothing on the page (the text simply lands one or two steps further out, in the same hue) and
 * fixes the chip on 10 of the 12 sampled backgrounds.
 *
 * The background gate is absolute; the surface gate can only ever improve the result. Where no
 * in-hue candidate clears both — mid-lightness backgrounds like `#1e6f9f` and `#0b6b3a`, where the
 * surface has travelled too far for even white to clear it — this returns exactly what it returned
 * before the surface gate existed. Chasing the surface there walks the lightness all the way to 1.0,
 * which is pure white whatever the hue was, and losing the hue is a worse outcome than the chip.
 */
export const deriveContentTextColor = (backgroundHex: string): string => {
  const background = tinycolor(backgroundHex);
  const {h, s, l} = background.toHsl();
  const baseLightness = l * 100;
  const goingLighter = isDarkLightness(l);
  const deepestSurface = shiftSurface(background, l, goingLighter, SURFACE_SHIFT_2);

  let firstLegibleOnBackground: string | null = null;

  for (let shift = SHIFT_START; shift <= SHIFT_MAX; shift += SHIFT_STEP) {
    const newLightness = goingLighter ? Math.min(100, baseLightness + shift) : Math.max(0, baseLightness - shift);
    const candidate = tinycolor({h, s, l: newLightness / 100});
    if (tinycolor.readability(background, candidate) < MIN_TEXT_CONTRAST) {
      continue;
    }
    firstLegibleOnBackground = firstLegibleOnBackground ?? candidate.toHexString();
    if (tinycolor.readability(deepestSurface, candidate) >= MIN_TEXT_CONTRAST) {
      return candidate.toHexString();
    }
  }

  return firstLegibleOnBackground ?? tinycolor.mostReadable(background, [PMW_COLORS_NEUTRAL.NEUTRAL_90, PMW_COLORS_NEUTRAL.NEUTRAL_0], {includeFallbackColors: true, level: 'AA', size: 'small'}).toHexString();
};

/**
 * Label color for text on a solid CTA fill. Design prefers white on colored buttons (matching the
 * primary design-system button — white on blue), so white is the first choice; the fallback is a
 * same-hue dark ink (a deep shade of the button's own color) rather than a flat neutral, so the
 * label still reads as part of the button. This is a perceptual rule, not raw max-contrast, which
 * would put black on saturated brand colors.
 *
 * Deliberately NOT contrast-gated: white on a saturated brand fill is often below 4.5:1 (the
 * design system's own primary button is), and forcing a dark label here would change every
 * existing event page's Register button. Mode A must stay byte-identical, so this rule is left
 * alone and any shortfall is *reported* rather than silently recolored.
 * Mode B, where the accent is machine-derived, uses `getDerivedAccentLabelColor` instead.
 */
export const getReadableButtonTextColor = (fillHex: string): string => {
  const fill = tinycolor(fillHex);
  if (fill.getLuminance() <= CTA_LIGHT_FILL_LUMINANCE) {
    return tinycolor(PMW_COLORS_NEUTRAL.NEUTRAL_0).toHexString();
  }
  const {h, s} = fill.toHsl();
  return tinycolor({h, s: s * CTA_INK_SATURATION_FACTOR, l: CTA_INK_LIGHTNESS}).toHexString();
};

/**
 * Label for a *derived* accent (Mode B). Prefers white to match the design-system button, but
 * measures it and falls back to a same-hue dark ink when white would not clear the gate.
 *
 * The gate matters here in a way it does not in Mode A: on a dark page the accent has to be light
 * to stay visible against the background (≥3:1), and a light fill cannot also carry a white label.
 * That conflict is unavoidable, so the label is what gives — e.g. the `#a07ff9` accent derived from
 * a dark purple background renders white at only 3.04:1 and gets dark purple ink instead. Unlike
 * Mode A, nothing pre-existing depends on this value, so it is safe to enforce.
 */
export const getDerivedAccentLabelColor = (accentHex: string): string => {
  const accent = tinycolor(accentHex);
  const white = tinycolor(PMW_COLORS_NEUTRAL.NEUTRAL_0);

  if (tinycolor.readability(accent, white) >= MIN_TEXT_CONTRAST) {
    return white.toHexString();
  }
  const {h, s} = accent.toHsl();
  const sameHueInk = tinycolor({h, s: s * CTA_INK_SATURATION_FACTOR, l: CTA_INK_LIGHTNESS});

  if (tinycolor.readability(accent, sameHueInk) >= MIN_TEXT_CONTRAST) {
    return sameHueInk.toHexString();
  }
  return tinycolor.mostReadable(accent, [PMW_COLORS_NEUTRAL.NEUTRAL_90, PMW_COLORS_NEUTRAL.NEUTRAL_0], {includeFallbackColors: true, level: 'AA', size: 'small'}).toHexString();
};

/** True when a color has no usable hue — black, white, and every grey in between. */
const isAchromatic = (colorHex: string): boolean => {
  return tinycolor(colorHex).toHsl().s <= ACHROMATIC_SATURATION_MAX;
};

/**
 * Hue and saturation for the derived accent.
 *
 * A chromatic background lends its own hue with a small saturation boost, so the button reads as a
 * vivid member of the page's colour family. An **achromatic** background has no hue to lend, and
 * `tinycolor` reports `h = 0` for it — boosting that phantom red is what turned a black page's
 * button brown (`#000000` → `#6f5252`).
 *
 * A greyscale page may therefore borrow a hue, but **only from the page text — the colour the styles
 * panel is currently showing.** An earlier version reached further back, to the colour Undo would
 * restore, so that black would produce a coloured button rather than a grey one. That inverted the
 * relationship the user can see: picking black re-derives the content colour to a grey, the swatch
 * says so, and the page then came out in a hue held over from whenever the background was first
 * picked — red on one calendar, and any other colour on the next. Borrowing from the visible text
 * means a page the user has left alone stays greyscale, and a hue appears only if they deliberately
 * choose a content colour on top of the background, where the swatch agrees with the page.
 */
const getAccentHueAndSaturation = (backgroundHex: string, hueSourceHex: string | null): {h: number; s: number} => {
  const {h, s} = tinycolor(backgroundHex).toHsl();
  if (!isAchromatic(backgroundHex)) {
    return {h, s: Math.min(1, s + ACCENT_SATURATION_BOOST)};
  }
  if (hueSourceHex !== null && !isAchromatic(hueSourceHex)) {
    const hueSource = tinycolor(hueSourceHex).toHsl();
    return {h: hueSource.h, s: hueSource.s};
  }
  return {h: 0, s: 0};
};

/**
 * Derive an accent/CTA color from a background: same hue family, nudged toward a
 * vivid mid lightness with a small saturation boost, then pushed until it reads
 * as a distinct surface against the background (plan §3.3b).
 *
 * `hueSourceHex` is only consulted when the background is achromatic — see `getAccentHueAndSaturation`.
 */
export const deriveAccentFromBackground = (backgroundHex: string, hueSourceHex: string | null = null): string => {
  const background = tinycolor(backgroundHex);
  const {l} = background.toHsl();
  const goingLighter = isDarkLightness(l);
  const {h, s: saturation} = getAccentHueAndSaturation(backgroundHex, hueSourceHex);

  let lightness = goingLighter ? Math.min(1, l + ACCENT_LIGHTEN) : Math.max(0, l - ACCENT_DARKEN);
  let accent = tinycolor({h, s: saturation, l: lightness});

  for (let iteration = 0; iteration < ACCENT_NUDGE_MAX_ITERATIONS && tinycolor.readability(background, accent) < MIN_ACCENT_CONTRAST; iteration += 1) {
    lightness = goingLighter ? Math.min(1, lightness + ACCENT_NUDGE_STEP) : Math.max(0, lightness - ACCENT_NUDGE_STEP);
    accent = tinycolor({h, s: saturation, l: lightness});
  }

  return accent.toHexString();
};

/**
 * The two surface shades, and which direction they travel.
 *
 * Normally they move *toward* the page text, which is what makes a card read as lifted off a dark
 * page and recessed into a light one. `deriveContentTextColor` gives the text enough margin to stay
 * legible on them, and on nearly every background that is the end of it.
 *
 * A mid-lightness background is the exception: the surface travels far enough that no text of that
 * hue clears 4.5:1 on it — `#0b6b3a` measured 2.26:1 on the deeper band. Those backgrounds do have
 * room to move the other way, so there the surfaces flip and travel *away* from the text instead.
 * The flip is deliberately last-resort: at the extremes (`#000000`, `#f7f9fc`) the away direction
 * has no room at all and would collapse both surfaces into the page, so it is only taken when the
 * normal direction has already failed and the room exists.
 */
const getSurfaceShades = (background: Instance, lightness01: number, goingTowardText: boolean, textHex: string): {light: string; lighter: string} => {
  const toward = {
    light: shiftSurface(background, lightness01, goingTowardText, SURFACE_SHIFT_1),
    lighter: shiftSurface(background, lightness01, goingTowardText, SURFACE_SHIFT_2),
  };
  const hasRoomToFlip = goingTowardText ? lightness01 - SURFACE_SHIFT_2 >= 0 : lightness01 + SURFACE_SHIFT_2 <= 1;

  if (tinycolor.readability(toward.lighter, textHex) >= MIN_TEXT_CONTRAST || !hasRoomToFlip) {
    return toward;
  }
  return {
    light: shiftSurface(background, lightness01, !goingTowardText, SURFACE_SHIFT_1),
    lighter: shiftSurface(background, lightness01, !goingTowardText, SURFACE_SHIFT_2),
  };
};

/**
 * Hover/active fill for a derived accent.
 *
 * It moves **away from the label**, not always darker. `darker` used to be the accent darkened by a
 * fixed amount, which quietly broke the one guarantee the label has: a dark ink loses contrast as
 * its fill darkens, so exactly the palettes that needed a dark ink failed the moment the pointer
 * arrived — `#00303f` went 5.61 → 3.56, `#fff4c2` 4.91 → 2.63. Choosing the direction from the ink
 * makes hover *increase* the label's contrast by construction (measured 6.8–9.0 across the sample),
 * and as a side effect fixes the accent-vs-page collapse on dark pages, where always-darkening
 * pushed the button back toward the background (`#4017af` 2.19 → 5.59).
 *
 * Lightness alone was not enough. Moving it far enough to read as a change cost light pages their
 * button-vs-page separation, because there the accent already sits close to the background. So the
 * step is split across two axes: a small lightness move away from the ink, plus a saturation boost.
 * The hover still reads as a distinct state, the label keeps its contrast, and a light page keeps
 * its separation.
 *
 * **A greyscale accent gets the lightness step only.** Saturating an achromatic fill is the same
 * phantom-red trap `getAccentHueAndSaturation` guards against, one function along: a black page
 * whose accent had no hue to inherit came out grey at rest and `#5d4747` — a muted crimson — the
 * moment a pointer touched any button on the page. There is no hue to deepen, so the boost invents
 * one; `ACHROMATIC_HOVER_SHIFT_PERCENT` carries the state change instead.
 */
const getAccentHoverColor = (accentHex: string, labelHex: string): string => {
  const labelIsDarkerThanFill = tinycolor(labelHex).getLuminance() < tinycolor(accentHex).getLuminance();
  const accentIsAchromatic = isAchromatic(accentHex);
  const shiftPercent = accentIsAchromatic ? ACHROMATIC_HOVER_SHIFT_PERCENT : ACCENT_HOVER_SHIFT_PERCENT;
  const shifted = labelIsDarkerThanFill ? lightenColor(accentHex, shiftPercent) : darkenColor(accentHex, shiftPercent);
  if (accentIsAchromatic) {
    return shifted;
  }
  const {h, s, l} = tinycolor(shifted).toHsl();
  return tinycolor({h, s: Math.min(1, s + ACCENT_HOVER_SATURATION_BOOST), l}).toHexString();
};

/**
 * Improved single-color (Mode A) palette. Keeps the Register fill equal to the
 * content color but derives a readable label and calendar-style surfaces, so it
 * no longer fails for white/light content colors (plan §3.3c). Kept alongside
 * the legacy `getNewStyleColors` as the selectable "Balanced" variant.
 */
export const getBalancedEventPalette = (contentHex: string): EventPaletteResult => {
  const container = getColorPaletteForContainer(contentHex);
  return {
    primary: contentHex,
    dark: getReadableTextColorForBackground(NEUTRAL_0_HEX, contentHex),
    darker: getDarkenedColorToneHex(contentHex),
    light: container.graphicContainerBg,
    lighter: container.backgroundColor,
    buttonTextColor: getReadableButtonTextColor(contentHex),
    background: null,
  };
};

/**
 * Themed (Mode B) palette seeded off a custom background. The provided
 * `textHex` is used verbatim as the page text (the caller decides whether it is
 * the user's content color or an auto-adjusted one); the accent and surfaces are
 * derived from the background.
 *
 * The page text doubles as the accent's hue source, which only matters for a greyscale background —
 * see `getAccentHueAndSaturation` for why it must be this colour and not a remembered one.
 */
export const getThemedEventPalette = (backgroundHex: string, textHex: string): EventPaletteResult => {
  const background = tinycolor(backgroundHex);
  const {l} = background.toHsl();
  const goingLighter = isDarkLightness(l);
  const accent = deriveAccentFromBackground(backgroundHex, textHex);
  const buttonTextColor = getDerivedAccentLabelColor(accent);

  return {
    primary: accent,
    dark: textHex,
    darker: getAccentHoverColor(accent, buttonTextColor),
    ...getSurfaceShades(background, l, goingLighter, textHex),
    buttonTextColor,
    background: backgroundHex,
  };
};

/**
 * Page colors for the events calendar. Mode A (no/white background) returns
 * `background: null` and no theme, so the caller renders exactly as today; Mode B paints the
 * shell, derives legible shell text, and returns the same themed palette the event page uses
 * so both surfaces share one derivation rather than two drifting ones.
 */
export const getCalendarPageColors = (backgroundHex: string | null, primaryHex: string): CalendarPageColors => {
  if (backgroundHex === null || isWhite(backgroundHex)) {
    return {background: null, text: primaryHex, theme: null};
  }
  const text = deriveContentTextColor(backgroundHex);
  return {background: backgroundHex, text, theme: getThemedEventPalette(backgroundHex, text)};
};

/**
 * What the styles panel should tell the user about the current background/text pairing.
 * Canonical definition — `@Panels/styles-input-panel` re-exports this rather than declaring its own,
 * so the event page and the events calendar can never disagree about what a status means.
 */
export enum LegibilityStatus {
  /** Nothing to say: no custom background, or the pairing is legible and untouched. */
  NONE = 'none',
  /** We auto-derived the text for the chosen background — offer Undo (green banner). */
  ADJUSTED = 'adjusted',
  /** The current pairing fails the gate — block saving and offer Fix (red banner). */
  POOR = 'poor',
  /**
   * Mode A only: the content color is legible as text on white, but it is also the CTA *fill*, and
   * as a fill it disappears into the page — a white pick renders the Register button invisible.
   *
   * Deliberately NOT `POOR`. `POOR` disables Done, and the save endpoint rejects a Mode B payload
   * that fails the gate; routing Mode A through it would make every event page already saved with a
   * pale brand color unsaveable on its next edit. D20 declined *changing* a pale Mode A fill for
   * exactly that reason, so this reports the problem and changes nothing.
   */
  WARNING = 'warning',
}

/**
 * The legibility state machine shared by the event wizard and the events-calendar editor.
 *
 * Both surfaces expose the same two inputs (a page background and a content color) and therefore
 * need the same rules, which are subtle enough that two hand-written copies would drift:
 *   - Choosing a background **always** re-derives the page text from it (decision D7), so the theme
 *     stays in one hue family instead of leaving an off-brand leftover.
 *   - The user's content color is not lost — it becomes the *reclaim* target behind the green
 *     banner's Undo. Undo restores it, which is the only path that can land on `POOR`.
 *   - Picking a content color while a background is set makes that color the page text directly,
 *     flagging `POOR` when it fails the gate.
 *   - Clearing the background returns to Mode A seeded off the reclaimed content color, never off
 *     the derived text (which would silently overwrite the user's brand color).
 *
 * These are pure transitions: a caller holds the state, calls a transition, then renders the
 * palette for `{background, contentColor}` and gates its save button on `status`.
 */
export interface ColorLegibilityState {
  /** Chosen page background; `null` is Mode A. */
  background: string | null;
  /** The color the page text currently uses. */
  contentColor: string;
  /** The content color to restore on Undo; `null` when there is nothing to reclaim. */
  colorToRestoreOnUndo: string | null;
  status: LegibilityStatus;
}

/**
 * The part of `ColorLegibilityState` a surface has to store. `background` and `contentColor` are
 * already in the palette it renders from; the the colour Undo restores and the banner status are not.
 *
 * Both surfaces keep this in Redux rather than component state, because on a small screen their
 * styles panel opens as a modal whose props are frozen when it opens — a component-state copy would
 * leave the modal's banner stuck and its handlers transitioning from stale colors.
 */
export interface LegibilityMemo {
  colorToRestoreOnUndo: string | null;
  status: LegibilityStatus;
}

export const getLegibilityMemo = (state: ColorLegibilityState): LegibilityMemo => {
  return {colorToRestoreOnUndo: state.colorToRestoreOnUndo, status: state.status};
};

/** The memo a surface starts from: nothing to restore, nothing to say. */
export const getDefaultLegibilityMemo = (): LegibilityMemo => {
  return {colorToRestoreOnUndo: null, status: LegibilityStatus.NONE};
};

/** The page a Mode A event renders on. Not user-configurable — picking white *is* what clears Mode B. */
const MODE_A_PAGE_BACKGROUND = '#ffffff';

/**
 * How far a Mode A CTA fill must stand off the white page before we stop calling it invisible.
 *
 * Deliberately NOT the 3:1 WCAG UI-component figure, and it does not claim to be an accessibility
 * gate. Since the label is now painted legibly on *any* fill, the only thing left to catch is the
 * fill dissolving into the page — so this answers "can you still see the button", nothing more. At
 * 3:1 it flagged PosterMyWall's own default brand blue (`#3FBCE8`, **2.19:1** on white), so every
 * untouched event page warned about itself.
 *
 * The value is a judgement on a continuum, not a cliff: measured against white, ordinary fills run
 * `#f1c40f` 1.66, `#cccccc` 1.61, `#ffd166` 1.44, `#FAD956` **1.39**, and pale ones continue down
 * through `#dddddd` 1.36, `#e0e0e0` 1.32, `#ededed` 1.17, `#f7f9fc` 1.06 to white 1.00 with no
 * natural break. 1.35 is anchored on the owner's call that `#FAD956` reads fine, clearing it by
 * 0.04 instead of failing it by 0.01. Everything the warning exists for is still caught — white,
 * near-whites and every pastel sit at 1.22 or below. Moving this line is a one-line change; the
 * tests either side of it are what stop it moving silently.
 */
const MIN_FILL_VISIBILITY_ON_WHITE = 1.35;

/** Same colour, whatever notation each side happens to be written in (`#3FBCE8` vs `#3fbce8`). */
const isSameColor = (a: string, b: string): boolean => {
  return tinycolor(a).toHexString() === tinycolor(b).toHexString();
};

/**
 * What to report about a pairing after a user-chosen color lands in the text slot.
 *
 * Mode B judges the color as page *text* against the custom background (4.5:1) — failing that is
 * `POOR`, which blocks the save. Mode A has no custom background, but the content color is still the
 * CTA *fill* on a white page, so it is judged only on whether it has disappeared into that page
 * (`MIN_FILL_VISIBILITY_ON_WHITE`); failing that is `WARNING`, which reports and blocks nothing.
 * See `LegibilityStatus.WARNING` for why the two must not merge.
 */
const getStatusForPairing = (background: string | null, contentColor: string): LegibilityStatus => {
  if (background !== null) {
    return isLegible(background, contentColor) ? LegibilityStatus.NONE : LegibilityStatus.POOR;
  }
  return isLegible(MODE_A_PAGE_BACKGROUND, contentColor, MIN_FILL_VISIBILITY_ON_WHITE) ? LegibilityStatus.NONE : LegibilityStatus.WARNING;
};

/**
 * Does this pick from the background picker *apply* a custom background, or clear one?
 *
 * The picker has no separate "remove" control — white is the off switch (see `applyBackgroundSelection`,
 * which reads a white pick as a return to Mode A). Analytics needs the distinction: counting a white pick
 * as a background application would report removals as applications, and the metric would stop being
 * comparable with how many pages are actually themed.
 */
export const appliesCustomBackground = (pickedBackground: string): boolean => {
  return !isWhite(pickedBackground);
};

/**
 * The user picked a page background. A white pick means "no custom background" and returns to Mode A
 * seeded off the reclaimed content color. Otherwise the text is re-derived and Undo is offered.
 */
export const applyBackgroundSelection = (state: ColorLegibilityState, pickedBackground: string): ColorLegibilityState => {
  if (isWhite(pickedBackground)) {
    const restored = state.colorToRestoreOnUndo ?? state.contentColor;
    return {background: null, contentColor: restored, colorToRestoreOnUndo: null, status: getStatusForPairing(null, restored)};
  }
  // Keep an existing capture rather than overwriting it with the previously derived text, so a second
  // background change still reclaims the user's own colour. Capturing whenever there is nothing yet —
  // not only on the Mode A → B crossing — is what gives a *saved* themed page a working Undo: it
  // rehydrates with no capture, having never crossed, and its Undo could otherwise restore nothing.
  const colorToRestoreOnUndo = state.colorToRestoreOnUndo ?? state.contentColor;
  const derivedText = deriveContentTextColor(pickedBackground);
  return {
    background: pickedBackground,
    contentColor: derivedText,
    colorToRestoreOnUndo,
    // Only claim an adjustment that happened. Deriving from a background often lands on the colour
    // already in the slot (black content on `#FB0000` derives back to black), and announcing that
    // offers an Undo with nothing to undo.
    status: isSameColor(derivedText, state.contentColor) ? LegibilityStatus.NONE : LegibilityStatus.ADJUSTED,
  };
};

/** The user picked a content color: it becomes the page text, and must stand on its own. */
export const applyContentSelection = (state: ColorLegibilityState, pickedContent: string): ColorLegibilityState => {
  return {
    background: state.background,
    contentColor: pickedContent,
    colorToRestoreOnUndo: pickedContent,
    status: getStatusForPairing(state.background, pickedContent),
  };
};

/** Undo the auto-adjustment: restore the user's content color, and report if it is now illegible. */
export const applyLegibilityUndo = (state: ColorLegibilityState): ColorLegibilityState => {
  const restored = state.colorToRestoreOnUndo ?? state.contentColor;
  return {...state, contentColor: restored, status: getStatusForPairing(state.background, restored)};
};

/** Fix a `POOR` pairing by re-deriving the text from the background. */
export const applyLegibilityFix = (state: ColorLegibilityState): ColorLegibilityState => {
  if (state.background === null) {
    return {...state, status: LegibilityStatus.NONE};
  }
  return {
    background: state.background,
    contentColor: deriveContentTextColor(state.background),
    colorToRestoreOnUndo: state.colorToRestoreOnUndo ?? state.contentColor,
    status: LegibilityStatus.ADJUSTED,
  };
};

/** Dismiss the informational banner. Only ever called for `ADJUSTED`; `POOR` must stay visible. */
export const dismissLegibilityBanner = (state: ColorLegibilityState): ColorLegibilityState => {
  return {...state, status: LegibilityStatus.NONE};
};
